<template>
  <div>
    <Teleport to="body">
    <transition name="modal" appear>
      <div class="modal-mask document-preview-modal" v-if="item && (item.id != null || item.uuid != null)">
        <div class="modal-safari-bleed" aria-hidden="true"></div>
        <div class="modal-safari-tint" aria-hidden="true"></div>
        <div class="modal-wrapper">
          <div class="modal-container">
            <div class="document-preview-shell">
              <div class="document-preview-chrome modal-header">
                <button type="button" class="document-preview-close" v-on:click="$emit('close')">
                  <img src="../../assets/img/icn_close.svg" alt="" class="document-preview-close-icon" aria-hidden="true">
                  <span>Close</span>
                </button>
              </div>
              <div class="document-preview-body">
                <div class="document-preview-content">
                  <div class="document-preview-logo-row">
                    <h1 class="document-preview-doctitle">INVOICE</h1>
                    <div class="document-preview-logo" aria-label="Logo">
                      <img v-if="settings && settings.images && settings.images.length" :src="getImageUrl({ images: settings.images })" alt="Company logo" class="document-preview-logo-img">
                    </div>
                  </div>
                  <div class="document-preview-header-info">
                    <div class="document-preview-to">
                      <div class="to-label">To:</div>
                      <div class="to-name">{{ item.contact && item.contact.name ? item.contact.name : '—' }}</div>
                      <div class="to-address" v-if="displayAddressLines && displayAddressLines.length">
                        <div v-for="(line, idx) in displayAddressLines" :key="idx">{{ line }}</div>
                      </div>
                    </div>
                    <div class="document-preview-company-details">
                      <div class="company-name">{{ settings && settings.business_name ? settings.business_name : '' }}</div>
                      <div class="company-line">{{ settings && settings.address && settings.address.address_line_1 ? settings.address.address_line_1 : '' }}</div>
                      <div class="company-line">{{ companySuburbStateLine }}</div>
                      <div class="company-field" v-if="settings && settings.phone">Phone: {{ settings.phone }}</div>
                      <div class="company-field" v-if="settings && settings.abn">ABN: {{ settings.abn }}</div>
                      <div class="company-field" v-if="settings && settings.licence_no">Licence No: {{ settings.licence_no }}</div>
                    </div>
                  </div>
                  <div class="document-preview-meta">
                    <div class="meta-item">
                      <span class="meta-label">Invoice #</span>
                      <span class="meta-value meta-number">{{ formatDocumentNumberShort(item.number) || '—' }}</span>
                    </div>
                    <div class="meta-item" v-if="item.service_m8_job_number != null && item.service_m8_job_number !== ''">
                      <span class="meta-label">SM8 Job #</span>
                      <span class="meta-value">
                        <img src="../../assets/img/icn_servicem8.png" style="height:14px;vertical-align:middle;margin-right:3px;">{{ item.service_m8_job_number }}
                      </span>
                    </div>
                    <div class="meta-item">
                      <span class="meta-label">Issued</span>
                      <input v-if="isDraft" type="date" class="meta-value meta-date-issued preview-editable" v-model="item.date">
                      <span v-else class="meta-value meta-date-issued">{{ formatDate(item.date) }}</span>
                    </div>
                    <div class="meta-item">
                      <span class="meta-label">Due Date</span>
                      <input v-if="isDraft" type="date" class="meta-value meta-date-due preview-editable" v-model="item.due_date">
                      <span v-else class="meta-value meta-date-due">{{ formatDate(item.due_date) }}</span>
                    </div>
                    <div class="meta-item meta-item-reference-row">
                      <span class="meta-label">Reference</span>
                      <span class="meta-value meta-reference">{{ item.reference || '—' }}</span>
                    </div>
                  </div>
                  <table class="document-preview-table">
                    <thead>
                      <tr>
                        <th>Items</th>
                        <th class="col-qty">Qty</th>
                        <th class="col-unit">Unit Price</th>
                        <th class="col-total">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, idx) in (item.items || [])" :key="idx">
                        <td>
                          <div class="item-title">{{ row.title }}</div>
                          <div class="item-description" v-if="lineItemDescription(row)">{{ lineItemDescription(row) }}</div>
                        </td>
                        <td class="col-qty">{{ parseInt(row.quantity != null ? row.quantity : (row.qty != null ? row.qty : 0), 10) }}</td>
                        <td class="col-unit">{{ currency(getUnitPrice(row), '$', 2) }}</td>
                        <td class="col-total">{{ currency(getLineTotal(row), '$', 2) }}</td>
                      </tr>
                      <tr v-if="hasAppliedDiscount" class="top aligned document-preview-discount-table-row">
                        <td>
                          <div class="item-title">Discount: {{ item.discount_title || '—' }}</div>
                          <div class="item-description" v-if="item.discount_description">{{ item.discount_description }}</div>
                        </td>
                        <td class="col-qty"></td>
                        <td class="col-unit"></td>
                        <td class="col-total">{{ currency(discountDisplayNegative, '$', 2) }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="document-preview-summary">
                    <div class="document-preview-totals-wrap">
                      <div class="document-preview-totals-right">
                        <div class="summary-row">
                          <span class="summary-label">Subtotal</span>
                          <span class="summary-value">{{ currency(item.sub_total, '$', 2) }}</span>
                        </div>
                        <div class="summary-row">
                          <span class="summary-label">GST</span>
                          <span class="summary-value">{{ currency(item.gst_total, '$', 2) }}</span>
                        </div>
                        <div class="summary-row summary-total">
                          <span class="summary-label">TOTAL</span>
                          <span class="summary-value">{{ currency(item.total, '$', 2) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="document-preview-terms">
                    <div class="terms-label">Terms and Conditions</div>
                    <textarea v-if="isDraft" class="terms-content preview-editable" rows="3" v-model="item.terms" placeholder="Thank you for your business..."></textarea>
                    <div v-else class="terms-content terms-readonly">{{ item.terms || '—' }}</div>
                  </div>
                  <div class="document-preview-signature" :class="{ 'is-unsigned': !item.invoice_signature_data }">
                    <img v-if="item.invoice_signature_data" :src="item.invoice_signature_data" class="signature-image" alt="">
                    <div class="signature-baseline" aria-hidden="true"></div>
                    <p v-if="item.invoice_signature_data" class="signature-caption">
                      Signed and Approved by {{ item.invoice_signed_by_name }}, {{ formatSignedAt(item.invoice_signed_at) }}
                    </p>
                    <p v-else class="signature-placeholder-label">Customer signature</p>
                  </div>
                </div>
                <div class="document-preview-actions">
                  <div class="status-dropdown">
                    <label>Status</label>
                    <app-select
                      :key="'invoice-status-select-' + statusSelectKey"
                      :items="statusOptions"
                      :value="item.status"
                      :disabled="isApproved"
                      @selected="onStatusChange">
                    </app-select>
                  </div>
                  <button type="button" class="action-btn negative" v-on:click="deleteInvoice">
                    <img src="../../assets/img/icn_trash.svg" alt="" class="action-icon" aria-hidden="true">
                    <span class="action-label">Delete</span>
                  </button>
                  <button type="button" class="action-btn primary-green" v-on:click="send">
                    <img src="../../assets/img/icn_envelope.svg" alt="" class="action-icon" aria-hidden="true">
                    <span class="action-label">Send</span>
                  </button>
                </div>
              </div>
            </div>
            <div class="modal-footer document-preview-footer-mobile">
              <div class="status-dropdown footer-status">
                <label>Status</label>
                <app-select
                  :key="'invoice-status-select-mobile-' + statusSelectKey"
                  :items="statusOptions"
                  :value="item.status"
                  :disabled="isApproved"
                  @selected="onStatusChange">
                </app-select>
              </div>
              <button type="button" class="action-btn negative" v-on:click="deleteInvoice">
                <img src="../../assets/img/icn_trash.svg" alt="" class="action-icon" aria-hidden="true">
                <span class="action-label">Delete</span>
              </button>
              <button type="button" class="action-btn primary-green" v-on:click="send">
                <img src="../../assets/img/icn_envelope.svg" alt="" class="action-icon" aria-hidden="true">
                <span class="action-label">Send</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
    </Teleport>
    <signature-accept-modal
      ref="signatureModal"
      :visible="signatureModalVisible"
      :customer-name="signatureCustomerName"
      :disclaimer="signatureDisclaimer"
      :can-submit="canSubmitSignature"
      :submitting="signatureSubmitting"
      @update:customerName="signatureCustomerName = $event"
      @update:signatureData="signatureData = $event"
      @update:hasSignature="hasSignature = $event"
      @cancel="cancelSignatureModal"
      @submit="submitSignature">
    </signature-accept-modal>
  </div>
</template>

<script>
import { clone } from "../../helpers/clone";
import axios from "axios";
import moment from "moment";
import { quoteTaskCustomerDescription } from "../../helpers/calculatedQuoteDisplay";
import { formatSuburbPostcodeStateLine } from "../../helpers/addressFormat";
import { formatDocumentNumberShort } from "../../helpers/documentNumberDisplay";
import { getQuoteLineUnitPrice, getQuoteLineTotal } from "../../helpers/quoteTotals";
import SignatureAcceptModal from "../../components/common/SignatureAcceptModal.vue";
import { isInvoiceStatusLocked } from "../../helpers/invoiceStatus";
import { formatDisplayDate } from "../../helpers/displayDate";

export default {
  components: { SignatureAcceptModal },
  props: { content: { type: Object } },
  data: () => ({
    item: {},
    saving: false,
    settings: {},
    statusSelectKey: 0,
    signatureModalVisible: false,
    signatureSubmitting: false,
    signatureCustomerName: '',
    signatureData: '',
    hasSignature: false,
    pendingStatusBeforeApprove: null,
    statuses: [
      { id: 'draft', name: 'Draft' },
      { id: 'sent', name: 'Sent' },
      { id: 'approved', name: 'Approved' }
    ]
  }),
  computed: {
    isSent() { return String((this.item && this.item.status) || '').toLowerCase() === 'sent'; },
    isDraft() { return String((this.item && this.item.status) || '').toLowerCase() === 'draft'; },
    isApproved() { return String((this.item && this.item.status) || '').toLowerCase() === 'approved'; },
    displayAddressLines() {
      const c = this.item && this.item.contact ? this.item.contact : {};
      const lines = [];
      if (c.street_address) lines.push(String(c.street_address));
      const subLine = formatSuburbPostcodeStateLine(c.suburb, c.postcode, c.state);
      if (subLine) lines.push(subLine);
      return lines;
    },
    companySuburbStateLine() {
      const a = this.settings && this.settings.address ? this.settings.address : {};
      return formatSuburbPostcodeStateLine(a.suburb, a.post_code, a.state);
    },
    signatureDisclaimer() {
      const company = (this.settings && this.settings.business_name) ? this.settings.business_name : 'your provider';
      return 'By signing, I approve this invoice and agree to all terms and conditions outlined by ' + company + '.';
    },
    canSubmitSignature() {
      return String(this.signatureCustomerName || '').trim() !== '' && this.hasSignature && !!this.signatureData;
    },
    statusOptions() {
      if (this.isApproved) return [{ id: 'approved', name: 'Approved' }];
      if (this.isSent) return [
        { id: 'sent', name: 'Sent' },
        { id: 'approved', name: 'Approved' }
      ];
      return [
        { id: 'draft', name: 'Draft' },
        { id: 'sent', name: 'Sent' },
        { id: 'approved', name: 'Approved' }
      ];
    },
    discountDisplay() {
      const raw = this.item && this.item.discount_amount_applied != null
        ? this.item.discount_amount_applied
        : (this.item && this.item.discount);
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : 0;
    },
    hasAppliedDiscount() {
      return this.discountDisplay > 0;
    },
    discountDisplayNegative() {
      const n = this.discountDisplay;
      return n > 0 ? -n : 0;
    }
  },
  methods: {
    lineItemDescription(row) { return quoteTaskCustomerDescription(row); },
    getUnitPrice(row) { return getQuoteLineUnitPrice(row); },
    getLineTotal(row) { return getQuoteLineTotal(row); },
    formatDocumentNumberShort,
    formatDate(val) {
      return formatDisplayDate(val);
    },
    formatSignedAt(val) {
      if (!val) return '';
      const m = moment(val);
      return m.isValid() ? m.format('D MMM YYYY [at] h:mm a') : '';
    },
    syncItem() {
      try {
        const c = this.content;
        this.item = (c && typeof c === 'object' && !Array.isArray(c)) ? clone(c) : {};
        if (!this.item.status) this.item.status = 'draft';
      } catch (_) {
        this.item = {};
      }
    },
    getSettings() {
      axios.get('/api/setting/display').then((response) => {
        this.settings = response.data || {};
      }).catch(() => {
        this.settings = {};
      });
    },
    onStatusChange($event) {
      if (this.isApproved || !($event && $event.id)) return;
      const previousStatus = String((this.item && this.item.status) || 'draft').toLowerCase();
      this.item.status = $event.id;
      if (String(this.item.status).toLowerCase() === 'approved') {
        this.pendingStatusBeforeApprove = previousStatus;
        this.openSignatureModal();
        return;
      }
      axios.patch(`/invoice/${this.item.uuid}`, { status: this.item.status }).catch(() => {});
    },
    openSignatureModal() {
      const c = this.item && this.item.contact ? this.item.contact : {};
      this.signatureCustomerName = [c.first_name, c.last_name].filter(Boolean).join(' ').trim()
        || (c.name ? String(c.name).trim() : '')
        || (c.company ? String(c.company).trim() : '');
      this.signatureData = '';
      this.hasSignature = false;
      this.signatureModalVisible = true;
      this.statusSelectKey++;
      this.$nextTick(() => {
        if (this.$refs.signatureModal && typeof this.$refs.signatureModal.prime === 'function') {
          this.$refs.signatureModal.prime();
        }
      });
    },
    cancelSignatureModal() {
      this.signatureModalVisible = false;
      this.signatureData = '';
      this.hasSignature = false;
      if (this.pendingStatusBeforeApprove) {
        this.item.status = this.pendingStatusBeforeApprove;
        this.pendingStatusBeforeApprove = null;
        this.statusSelectKey++;
      }
    },
    submitSignature() {
      if (!this.canSubmitSignature || this.signatureSubmitting) return;
      const signedByName = String(this.signatureCustomerName || '').trim();
      const signatureData = String(this.signatureData || '').trim();
      this.signatureSubmitting = true;
      const approvePayload = {
        status: 'approved',
        invoice_signed_by_name: signedByName,
        invoice_signature_data: signatureData
      };
      axios.patch(`/invoice/${this.item.uuid}`, approvePayload).then((response) => {
        this.signatureModalVisible = false;
        this.pendingStatusBeforeApprove = null;
        this.item.status = 'approved';
        this.$set(this.item, 'invoice_signed_by_name', signedByName);
        this.$set(this.item, 'invoice_signature_data', signatureData);
        this.$set(this.item, 'invoice_signed_at', moment().format('YYYY-MM-DD HH:mm:ss'));
        if (this.successNotification) this.successNotification();
        const sync = response && response.data && response.data.xero_sync;
        if (sync && sync.synced === false && this.errorNotification) {
          this.errorNotification('Invoice approved, but Xero sync failed: ' + (sync.reason || 'Unknown error'));
        }
      }).catch((err) => {
        const msg = err.response && err.response.data && err.response.data.error
          ? err.response.data.error
          : 'Failed to approve invoice';
        if (this.errorNotification) this.errorNotification(msg);
        if (this.pendingStatusBeforeApprove) {
          this.item.status = this.pendingStatusBeforeApprove;
          this.pendingStatusBeforeApprove = null;
        }
        this.statusSelectKey++;
      }).finally(() => {
        this.signatureSubmitting = false;
      });
    },
    send() {
      if (this.isSent || this.isApproved || isInvoiceStatusLocked(this.item)) {
        this.$emit('sendOption', this.item);
        return;
      }
      this.saving = true;
      const toIso = (val) => {
        if (!val) return null;
        if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val.trim())) return val.trim();
        const m = moment(val);
        return m.isValid() ? m.format('YYYY-MM-DD') : null;
      };
      const payload = { terms: this.item.terms };
      const d = toIso(this.item.date);
      if (d) payload.date = d;
      const dueDate = toIso(this.item.due_date);
      if (dueDate) payload.due_date = dueDate;
      axios.patch(`/invoice/${this.item.uuid}`, payload).then(() => {
        this.$emit('sendOption', this.item);
      }).catch(() => {
        if (this.errorNotification) this.errorNotification('Failed to save invoice before sending');
      }).finally(() => {
        this.saving = false;
      });
    },
    deleteInvoice() {
      this.swal({
        title: 'Are you sure?',
        text: `Delete invoice ${this.formatDocumentNumberShort(this.item.number)}`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!'
      }).then((result) => {
        if (result.value) {
          axios.delete(`/invoice/${this.item.uuid}`).then(() => {
            this.$emit('close', { refresh: true });
          });
        }
      });
    }
  },
  mounted() {
    this.syncItem();
    this.getSettings();
    document.documentElement.classList.add('document-preview-open')
    document.body.classList.add('document-preview-open')
  },
  beforeUnmount() {
    document.documentElement.classList.remove('document-preview-open')
    document.body.classList.remove('document-preview-open')
  },
  beforeUnmount() {
    document.documentElement.classList.remove('document-preview-open')
    document.body.classList.remove('document-preview-open')
  },
  watch: {
    content: {
      handler() { this.syncItem(); },
      deep: true
    }
  }
}
</script>
